import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const FeatureContext = React.createContext({});

/** Feature flag data model:
 * override = If `true`, it will always display the feature.
 * status = If `FEATURE_STATUS.Development`, the feature is only visible if the user overrides. If `FEATURE_STATUS.Internal`, all staff users can see the feature. If `FEATURE_STATUS.All` the feature may be visible to end users depending on the rules. Otherwise, the feature will be hidden by default unless overriden or otherwise specified by configured rules.
 * rules = An object of options to configure. Defaults to `null` if not provided,
 * rules options = {
 *   includeCountries = Allow-list of country codes this feature is limited to. When set, the feature is only available to end users in these countries. Takes precedence over excludeCountries. Defaults to null
 *   excludeCountries = Deny-list of country codes that will exclude this feature from end users. Defaults to null
 * }
 */

export const FEATURE_STATUS = {
  Development: 'Development',
  Internal: 'Internal',
  All: 'All',
};

export const createFeature = ({ status, rules }) => {
  return {
    status,
    override: false,
    rules: rules
      ? {
          includeCountries: rules.includeCountries || null,
          excludeCountries: rules.excludeCountries || null,
        }
      : null,
  };
};

const DISALLOWED_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
const SAFE_KEY_REGEX = /^[A-Za-z0-9_-]{1,50}$/;

const isSafeFeatureKey = key =>
  typeof key === 'string' && SAFE_KEY_REGEX.test(key) && !DISALLOWED_KEYS.has(key);

const FeatureFlagProvider = ({ children, defaultFeatures }) => {
  const { search } = useLocation();
  const [flags, setFlags] = useState(defaultFeatures || {});

  useEffect(() => {
    const featureFlag = new URLSearchParams(search).get('feature');
    if (featureFlag && isSafeFeatureKey(featureFlag)) {
      setFlags(prev => {
        const copyFlags = Object.assign(Object.create(null), prev);

        if (!Object.prototype.hasOwnProperty.call(copyFlags, featureFlag)) {
          copyFlags[featureFlag] = {
            override: true,
            status: FEATURE_STATUS.Development,
            rules: null,
          };
        } else {
          copyFlags[featureFlag] = {
            ...copyFlags[featureFlag],
            override: true,
          };
        }

        return copyFlags;
      });
    }

    if (featureFlag === 'null') {
      setFlags(defaultFeatures || {});
    }
  }, [search, defaultFeatures]);

  return <FeatureContext.Provider value={flags}>{children}</FeatureContext.Provider>;
};

export default FeatureFlagProvider;
